import { useEffect, useRef, useCallback } from 'react'
import { projectApi } from '../api/client'
import type { Video, Segment } from '../types'

/**
 * Auto-saves project state to the server whenever video, segments,
 * or selections change. Debounced to avoid hammering the API.
 *
 * Returns:
 * - projectId: the saved project ID (set after first save)
 * - isSaving: true while a save is in flight
 */
export function useProjectAutoSave({
  video,
  segments,
  selectedSegments,
  analyzeJobId,
  pipelineVideoUrl,
  enabled = true,
}: {
  video: Video | null
  segments: Segment[]
  selectedSegments: string[]
  analyzeJobId: string | null
  pipelineVideoUrl: string | null
  enabled?: boolean
}) {
  const projectIdRef = useRef<string | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSavingRef = useRef(false)

  // Save project + segments (debounced)
  const saveNow = useCallback(async () => {
    if (!video || !enabled) return
    if (isSavingRef.current) return
    isSavingRef.current = true

    try {
      const projectData = {
        id: projectIdRef.current || video.id,
        title: video.title,
        video_url: pipelineVideoUrl || video.url,
        duration: video.duration,
        thumbnail: video.thumbnail,
        platform: video.platform,
        metadata: {
          analyze_job_id: analyzeJobId,
          pipeline_video_url: pipelineVideoUrl,
          original_url: video.url,
        },
        segments: segments.map((seg, i) => ({
          id: seg.id,
          start: seg.start,
          end: seg.end,
          text: seg.text,
          highlight_score: seg.highlight_score ?? seg.confidence,
          tags: seg.tags,
          padding: seg.padding,
          speaker: seg.speaker,
          color: seg.color,
          is_manual: seg.is_manual,
          is_selected: selectedSegments.includes(seg.id),
        })),
      }

      const result = await projectApi.saveProject(projectData)
      if (result.success && result.project?.id) {
        projectIdRef.current = result.project.id
      }
    } catch (err) {
      console.warn('Auto-save failed (non-fatal):', err)
    } finally {
      isSavingRef.current = false
    }
  }, [video, segments, selectedSegments, analyzeJobId, pipelineVideoUrl, enabled])

  // Debounced trigger — saves 2s after last change
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(saveNow, 2000)
  }, [saveNow])

  // Auto-save when segments or selections change
  useEffect(() => {
    if (!video || segments.length === 0 || !enabled) return
    scheduleSave()
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [segments, selectedSegments, enabled, scheduleSave, video])

  // Initial save when video is first loaded
  useEffect(() => {
    if (video && enabled) {
      projectIdRef.current = video.id
      // Don't save immediately on load — wait for segments
    }
  }, [video?.id, enabled])

  return {
    projectId: projectIdRef.current,
    saveNow,
  }
}
